/**
 * Where to send a user once they are signed in.
 *
 * `ProtectedRoute` bounces an unauthenticated visitor to `/login` with
 * `state: { from: location }`. Until #617 nothing read that back, so the
 * post-login redirect always landed on `/dashboard` — fine for a bookmark, but
 * it silently threw away a one-shot URL. The case that made it matter is the
 * Revolut OAuth callback: Revolut redirects to
 * `/integrations/revolut/callback?code=…`, and if that arrives in a browser
 * with no session the `code` is gone by the time the user reaches the
 * dashboard. (The code lives ~2 minutes, so it may well have expired anyway —
 * the callback's own "start again" message covers that; losing the URL means
 * the user never even sees which failure they hit.)
 *
 * Pure so it can be unit-tested: it is the only place that decides whether a
 * remembered location is safe to navigate to.
 */

/** The subset of react-router's `Location` this needs; also what a raw
 *  `history.state` blob may look like after a reload. */
export interface RedirectLocationLike {
  pathname?: unknown
  search?: unknown
}

/**
 * `true` for an in-app absolute path. `//evil.example` and
 * `https://evil.example` are both rejected: the browser treats a
 * protocol-relative path as a different origin, so echoing an attacker-chosen
 * `from` into `navigate()` would be an open redirect.
 */
function isSafeInAppPath(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')
}

/**
 * Resolve the location a login flow should return to, or `fallback` when the
 * router state carries nothing usable. `search` is preserved — for the Revolut
 * callback the query string *is* the payload.
 */
export function postLoginRedirect(state: unknown, fallback: string): string {
  if (!state || typeof state !== 'object') return fallback
  const from = (state as { from?: unknown }).from
  if (!from || typeof from !== 'object') return fallback

  const { pathname, search } = from as RedirectLocationLike
  if (typeof pathname !== 'string' || !isSafeInAppPath(pathname)) return fallback

  const query = typeof search === 'string' ? search : ''
  return `${pathname}${query}`
}

/**
 * `true` when the router state names a real return target (i.e.
 * `postLoginRedirect` would not fall back). Lets a caller navigate only when
 * there is somewhere specific to go and otherwise leave the routing to
 * `PublicRoute`.
 */
export function hasPostLoginRedirect(state: unknown): boolean {
  const sentinel = ' none'
  return postLoginRedirect(state, sentinel) !== sentinel
}
